/**
 * Axios Configuration with Token Refresh Interceptor
 *
 * This module sets up axios interceptors to handle:
 * 1. 401 errors due to expired Firebase tokens
 * 2. Auto-refresh token and retry failed requests
 * 3. Show notification if refresh fails
 */

import axios from 'axios';
import { auth } from '../firebase';
import { toast } from 'react-toastify';

// Store for pending requests that need token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Attempt to refresh the Firebase token
 */
const refreshFirebaseToken = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('No user logged in');
  }

  // Force refresh the token
  const newToken = await currentUser.getIdToken(true);
  return newToken;
};

/**
 * Setup axios interceptors for global error handling
 * Call this once in App.js or index.js
 */
export const setupAxiosInterceptors = (store) => {
  // Response interceptor to handle 401 errors
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Check if it's a 401 error and not already retried
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Check if the error is specifically about token expiration
        const errorMessage = error.response?.data?.error || error.response?.data?.message || '';
        const isTokenExpired =
          errorMessage.includes('token') &&
          (errorMessage.includes('expired') || errorMessage.includes('invalid'));

        if (isTokenExpired || error.response?.data === 'Unauthorized') {
          if (isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers['authtoken'] = token;
              originalRequest.headers['authToken'] = token;
              return axios(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const newToken = await refreshFirebaseToken();

            // Update the token in Redux store
            if (store) {
              store.dispatch({
                type: 'REFRESH_TOKEN',
                payload: { token: newToken }
              });
            }

            processQueue(null, newToken);

            // Retry the original request with new token
            originalRequest.headers['authtoken'] = newToken;
            originalRequest.headers['authToken'] = newToken;

            return axios(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);

            // Token refresh failed - notify user
            toast.error('Your session has expired. Please refresh the page to continue.', {
              position: 'top-center',
              autoClose: false,
              closeOnClick: true,
              toastId: 'session-expired', // Prevent duplicate toasts
            });

            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
      }

      return Promise.reject(error);
    }
  );

  // Request interceptor to add latest token to all requests
  axios.interceptors.request.use(
    async (config) => {
      // Only add token if it's an API request to our backend
      if (config.url?.includes(process.env.REACT_APP_API)) {
        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            // Get current token (Firebase caches it if not expired)
            const token = await currentUser.getIdToken();
            config.headers['authtoken'] = token;
            config.headers['authToken'] = token;
          } catch (error) {
            console.error('Failed to get token for request:', error);
          }
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export default axios;
